import { GitHubIcon } from "@insights/ui";
import {
  Activity,
  Cable,
  Clock3,
  CreditCard,
  GitBranch,
  ReceiptText,
  ShieldCheck,
  Settings,
  SquareTerminal,
  UserRound,
  Users,
  SlidersHorizontal,
  GitCommitHorizontal,
  FileText,
  HeartPulse,
  HardDrive,
  Waypoints,
  MessageSquareMore,
  Rocket,
  ScanSearch,
  LayoutDashboard,
  UsersRound,
  BookOpen,
  CircleHelp,
  PlugZap,
  ChartNoAxesCombined,
  Library,
  Inbox,
} from "lucide-react";
import type { ComponentType, SVGAttributes } from "react";
import type { WorkspaceFeature, WorkspaceFeatures } from "@insights/contracts";
import { matchPath } from "react-router";

type WorkspaceNavigationIcon = ComponentType<SVGAttributes<SVGSVGElement>>;

export interface WorkspaceSection {
  icon: WorkspaceNavigationIcon;
  label: string;
  path: string;
  matchDescendants?: boolean;
  ownerOnly?: boolean;
  feature?: WorkspaceFeature;
}

export interface WorkspaceTopic {
  icon: WorkspaceNavigationIcon;
  label: string;
  sections: [WorkspaceSection, ...WorkspaceSection[]];
}

export const workspaceTopics: WorkspaceTopic[] = [
  {
    icon: Activity,
    label: "Builds",
    sections: [
      {
        icon: Clock3,
        label: "Latest",
        path: "/w/:workspaceId/builds",
        matchDescendants: true,
      },
      {
        icon: GitBranch,
        label: "Branch or Tag",
        path: "/w/:workspaceId/builds/branch-or-tag",
      },
      {
        icon: GitCommitHorizontal,
        label: "Timeline",
        path: "/w/:workspaceId/builds/timeline",
      },
      {
        icon: FileText,
        label: "Comparison",
        path: "/w/:workspaceId/builds/comparison",
      },
    ],
  },
  {
    icon: HeartPulse,
    label: "Pulse",
    sections: [
      {
        icon: Inbox,
        label: "Inbox",
        path: "/w/:workspaceId/pulse/inbox",
        feature: "decisionInbox",
      },
      {
        icon: LayoutDashboard,
        label: "Activity",
        path: "/w/:workspaceId/pulse/activity",
      },
      {
        icon: UsersRound,
        label: "Involvement",
        path: "/w/:workspaceId/pulse/involvement",
      },
      {
        icon: Waypoints,
        label: "Flow",
        path: "/w/:workspaceId/pulse/flow",
      },
      {
        icon: MessageSquareMore,
        label: "Feedback",
        path: "/w/:workspaceId/pulse/feedback",
      },
      {
        icon: Rocket,
        label: "Delivery",
        path: "/w/:workspaceId/pulse/delivery",
      },
      {
        icon: ScanSearch,
        label: "Behavior",
        path: "/w/:workspaceId/pulse/behavior",
      },
    ],
  },
  {
    icon: Cable,
    label: "Integrations",
    sections: [
      {
        icon: SquareTerminal,
        label: "Terrabuild",
        path: "/w/:workspaceId/integration/terrabuild",
      },
      {
        icon: GitHubIcon,
        label: "GitHub",
        path: "/w/:workspaceId/github",
      },
    ],
  },
  {
    icon: Settings,
    label: "Settings",
    sections: [
      {
        icon: Settings,
        label: "General",
        path: "/w/:workspaceId/general",
      },
      {
        icon: Users,
        label: "Teams",
        path: "/w/:workspaceId/teams",
      },
      {
        icon: UserRound,
        label: "Profile",
        path: "/w/:workspaceId/profile",
      },
      {
        icon: ShieldCheck,
        label: "Security",
        path: "/w/:workspaceId/security",
      },
      {
        icon: SlidersHorizontal,
        label: "Release Rules",
        path: "/w/:workspaceId/release-rules",
      },
      {
        icon: HardDrive,
        label: "Cache",
        path: "/w/:workspaceId/cache",
      },
      {
        icon: CreditCard,
        label: "Billing",
        path: "/w/:workspaceId/billing",
        ownerOnly: true,
      },
      {
        icon: ReceiptText,
        label: "Invoices",
        path: "/w/:workspaceId/invoices",
        ownerOnly: true,
      },
    ],
  },
  {
    icon: BookOpen,
    label: "Documentation",
    sections: [
      {
        icon: CircleHelp,
        label: "Overview",
        path: "/w/:workspaceId/docs/overview",
      },
      {
        icon: PlugZap,
        label: "Connect Data",
        path: "/w/:workspaceId/docs/connect",
      },
      {
        icon: ChartNoAxesCombined,
        label: "Builds",
        path: "/w/:workspaceId/docs/builds",
      },
      {
        icon: Waypoints,
        label: "Pulse",
        path: "/w/:workspaceId/docs/pulse",
      },
      {
        icon: Library,
        label: "Workspace",
        path: "/w/:workspaceId/docs/workspace",
      },
    ],
  },
];

export function getWorkspaceTopics(
  role: string | undefined,
  features?: WorkspaceFeatures,
) {
  return workspaceTopics.map((topic) => ({
    ...topic,
    sections: topic.sections.filter(
      (section) =>
        (!section.ownerOnly || role === "owner")
        && (!section.feature || features?.[section.feature] === true),
    ) as [WorkspaceSection, ...WorkspaceSection[]],
  }));
}

export function buildWorkspacePath(path: string, workspaceId: string) {
  return path.replace(":workspaceId", workspaceId);
}

export function buildDefaultWorkspacePath(workspaceId: string) {
  return `/w/${workspaceId}/builds`;
}

export function getActiveWorkspaceNavigation(
  pathname: string,
  role?: string,
  features?: WorkspaceFeatures,
) {
  const sections = getWorkspaceTopics(role, features).flatMap((topic) =>
    topic.sections.map((section) => ({ section, topic })),
  );

  for (const includeDescendants of [false, true]) {
    for (const { section, topic } of sections) {
      if (includeDescendants && !section.matchDescendants) {
        continue;
      }

      const match = matchPath(
        {
          path: section.path,
          end: !includeDescendants,
        },
        pathname,
      );

      if (match) {
        return {
          section,
          topic,
        };
      }
    }
  }

  return undefined;
}
